// Вход — общий для штаба и контрибьюторов: человек не обязан помнить, в какой
// он таблице, а роль решает сервер.
//
// Сейчас дверей две: пароль и magic-link через #/auth?token=… Вход через Google
// ВЫКЛЮЧЕН по решению Tim (14.08) — «пока не нужен, пусть будет только логин
// пароль». Код входа через Google жив (и на воркере, и здесь), но кнопка
// скрыта: чтобы вернуть, соберите фронт с VITE_GOOGLE_LOGIN=1 и задайте на
// воркере GOOGLE_CLIENT_ID/SECRET. Возвращая — помните: Google здесь НЕ
// регистрация, почта, которую не завёл админ, получает отказ (ноль доступа по
// умолчанию).
import { useEffect, useState } from 'preact/hooks';
import { Turnstile } from '../components/Turnstile';
import { api, API_BASE, setToken } from '../api';
import { InstallPrompt } from '../components/InstallPrompt';
import { asLang, makeT } from '../i18n';

// До входа язык профиля неизвестен — берём язык браузера (после входа
// контрибьютора ведёт язык из профиля, как и раньше).
const tGuest = makeT(asLang(navigator.language.slice(0, 2)));

/** Кнопка «Войти через Google». Выключена (см. шапку файла): сборка без
 *  VITE_GOOGLE_LOGIN=1 не показывает ни кнопку, ни ошибки OAuth-редиректа. */
const GOOGLE_LOGIN = (import.meta.env.VITE_GOOGLE_LOGIN as string | undefined) === '1';

/** Текст ошибки из редиректа воркера (?error=…). Человеку нужно понять, что
 *  делать дальше, а не увидеть код. */
const OAUTH_ERROR: Record<string, string> = {
  no_access:
    'Этой почты нет в системе. Доступ выдаёт админ — попроси добавить именно тот адрес, которым ты входишь.',
  google_not_configured: 'Вход через Google ещё не настроен на этом стенде — войди по паролю.',
  google_denied: 'Google не подтвердил вход.',
  email_unverified: 'Google говорит, что почта не подтверждена.',
  bad_state: 'Ссылка входа устарела — попробуй ещё раз.',
  token_exchange: 'Google не ответил на запрос входа — попробуй ещё раз.',
};

export function LoginPage({ authToken }: { authToken?: string }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Токен проверки «человек ли это». Пустой — нормально: пока ключ сайта не
  // задан, виджета нет и сервер проверку пропускает.
  const [tsToken, setTsToken] = useState('');
  const [state, setState] = useState<'idle' | 'busy' | 'error'>(authToken ? 'busy' : 'idle');
  // Воркер приводит сюда с #/login?error=… после неудачного Google-входа.
  const oauthError = GOOGLE_LOGIN
    ? new URLSearchParams(location.hash.split('?')[1] ?? '').get('error')
    : null;

  useEffect(() => {
    if (!authToken) return;
    api
      .verify(authToken)
      .then((r) => {
        setToken(r.token);
        location.hash = '#/';
        // App fetched `me` once on mount — before this token existed — and
        // cached null. Without a reload the user lands back on the login form
        // with a valid session in localStorage. Mirrors the logout path.
        location.reload();
      })
      .catch(() => setState('error'));
  }, [authToken]);

  const submit = async (e: Event) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setState('busy');
    try {
      const r = await api.login(email.trim(), password, tsToken || undefined);
      setToken(r.token);
      location.hash = '#/';
      location.reload();
    } catch {
      setState('error');
    }
  };

  if (authToken && state === 'busy') {
    return (
      <div class="login-page">
        <div class="login-card wb-card">
          <p class="lede">{tGuest('login.signingIn')}</p>
        </div>
      </div>
    );
  }

  return (
    <div class="login-page">
      <div class="login-card wb-card">
        <img src="/logo/weern-mark-green.svg" alt="" class="login-logo" />
        <h1>Vista</h1>
        <p class="lede">{tGuest('login.lede')}</p>

        {oauthError && (
          <div class="msg err">{OAUTH_ERROR[oauthError] ?? 'Войти через Google не получилось.'}</div>
        )}

        {/* Обычная ссылка, а не fetch: OAuth — это редирект браузера целиком,
            и XHR тут ничего не даст. */}
        {GOOGLE_LOGIN && (
          <>
            <a class="btn-primary block" href={`${API_BASE}/api/vista/auth/google/start`}>
              Войти через Google
            </a>
            <div class="or">или по паролю</div>
          </>
        )}

        <form onSubmit={submit}>
          <input
            class="input"
            type="email"
            placeholder={tGuest('login.email')}
            value={email}
            autocomplete="username"
            onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
          />
          <input
            class="input"
            type="password"
            placeholder={tGuest('login.password')}
            value={password}
            autocomplete="current-password"
            onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
          />
          {/* Виджет рисуется, только если задан ключ сайта — иначе его нет и
              форма работает как раньше (см. components/Turnstile.tsx). */}
          <Turnstile onToken={setTsToken} />
          <button
            class={`${GOOGLE_LOGIN ? 'btn-ghost' : 'btn-primary'} block`}
            disabled={state === 'busy' || !email.trim() || !password}
          >
            {state === 'busy' ? tGuest('login.signingIn') : tGuest('login.submit')}
          </button>
        </form>
        {state === 'error' && (
          <div class="msg err">{tGuest('login.failed')}</div>
        )}

        {/* Установка на телефон предлагается ДО входа: контрибьютор ставит
            приложение по инвайт-ссылке, ещё не имея сессии. */}
        <InstallPrompt t={tGuest} />
        <div class="build-tag">
          {tGuest('login.build')} {(import.meta.env.VITE_BUILD_TAG as string | undefined) ?? 'dev'}
        </div>
      </div>
    </div>
  );
}
